import { useQuery } from '@tanstack/react-query';

import type {
  AssignableUser,
  BoardView,
  BoardWithWorkflow,
  Department,
  DirectoryUser,
  JobRole,
  Project,
} from '../api/endpoints.js';
import { boardApi, orgApi, ticketApi, userApi } from '../api/endpoints.js';
import { queryKeys } from '../app/query-client.js';

const LONG = { staleTime: 5 * 60_000 } as const;

export function useBoards(includeInactive = false): {
  readonly boards: readonly BoardWithWorkflow[];
  readonly isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.boards.list(includeInactive),
    queryFn: () => boardApi.list(includeInactive),
    staleTime: 60_000,
  });

  return { boards: data?.items ?? [], isLoading };
}

export function useBoardView(boardId: string | undefined): {
  readonly view: BoardView | undefined;
  readonly isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.boards.cards(boardId ?? 'none'),
    queryFn: () => ticketApi.board(boardId ?? ''),
    enabled: boardId !== undefined,
    staleTime: 10_000,
  });

  return { view: data, isLoading };
}

export function useArchivedCards(
  boardId: string | undefined,
  enabled: boolean,
): { readonly view: BoardView | undefined; readonly isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.boards.archived(boardId ?? 'none'),
    queryFn: () => ticketApi.board(boardId ?? '', 'only'),
    enabled: enabled && boardId !== undefined,
    staleTime: 10_000,
  });

  return { view: data, isLoading };
}

export function useJobRoles(includeInactive = false): {
  readonly jobRoles: readonly JobRole[];
  readonly isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.org.jobRoles.list(includeInactive),
    queryFn: () => orgApi.jobRoles(includeInactive),
    ...LONG,
  });

  return { jobRoles: data?.items ?? [], isLoading };
}

export function useDepartments(includeInactive = false): {
  readonly departments: readonly Department[];
  readonly isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.org.departments.list(includeInactive),
    queryFn: () => orgApi.departments(includeInactive),
    ...LONG,
  });

  return { departments: data?.items ?? [], isLoading };
}

export function useProjects(): readonly Project[] {
  const { data } = useQuery({
    queryKey: queryKeys.org.projects,
    queryFn: () => orgApi.projects(),
    ...LONG,
  });

  return data?.items ?? [];
}

export function useDirectory(): readonly DirectoryUser[] {
  const { data } = useQuery({
    queryKey: queryKeys.org.users,
    queryFn: userApi.directory,
    ...LONG,
  });

  return data?.items ?? [];
}

export function useAssignableUsers(departmentId: string | null): readonly AssignableUser[] {
  const { data } = useQuery({
    queryKey: queryKeys.org.assignable(departmentId ?? 'none'),
    queryFn: () => userApi.assignable(departmentId ?? ''),
    enabled: departmentId !== null,
    ...LONG,
  });

  return data?.items ?? [];
}

export function useDirectoryIndex(): ReadonlyMap<string, DirectoryUser> {
  const people = useDirectory();
  return new Map(people.map((person) => [person.id, person]));
}
